import { K, mono } from '../lib/theme'

const S = ({ h, children }: { h: string; children: React.ReactNode }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 8px', letterSpacing: '-.005em' }}>{h}</h2>
    <div style={{ fontSize: 13.5, color: K.mut, lineHeight: 1.6, maxWidth: '78ch' }}>{children}</div>
  </section>
)
const M = ({ children }: { children: React.ReactNode }) => <span style={{ fontFamily: mono, color: K.txt }}>{children}</span>

export function Method() {
  return (
    <main style={{ padding: '28px clamp(16px,3vw,28px) 0', maxWidth: 1240, margin: '0 auto' }}>
      <h1 style={{ fontSize: 'clamp(22px,2.6vw,30px)', fontWeight: 500, margin: '0 0 6px', letterSpacing: '-.015em' }}>
        How a number on Offbell is produced, and when it is withheld
      </h1>
      <p style={{ color: K.dim, fontSize: 13, margin: '0 0 28px', fontFamily: mono }}>
        Written so a sceptic can use it to attack a number we published.
      </p>

      <S h="The on-chain price">
        Read from the token's Uniswap V3 pool on Robinhood Chain via <M>slot0()</M>, converted from
        <M> sqrtPriceX96</M> to ETH per whole share. Uniswap orders a pair by address, so which side WETH falls on decides
        whether the raw ratio is inverted — getting that backwards yields a plausible price that is wrong by orders of
        magnitude, so the token ordering is checked per pool rather than assumed.
      </S>

      <S h="The USD conversion">
        Pools price in ETH; equities price in USD. We convert with a spot ETH/USD rate from Coinbase, sampled at the same
        time as the pool read. Without this step a "premium" would partly be measuring ETH's own movement rather than the
        token's.
      </S>

      <S h="The reference price">
        The last regular-session print for the underlying equity, with its own timestamp. This is the half of the
        calculation we do not control, so its age travels with it everywhere on the site. A premium computed against a
        three-day-old close is a different claim from one computed mid-session, and the two are never averaged together.
      </S>

      <S h="The premium">
        <M>(on-chain − reference) / reference</M>, expressed in basis points. One basis point is 0.01%. Positive means the
        token is trading above the last real print; negative means below.
      </S>

      <S h="When a number is withheld">
        We print nothing rather than something misleading, in four cases:
        <ul style={{ margin: '10px 0 0', paddingLeft: 20 }}>
          <li><M>no public reference</M> — the token tracks a private company. SPCX (SpaceX) has no public last print, so
            there is nothing to subtract from. It is listed with its price and no premium, permanently.</li>
          <li><M>reference stale beyond tolerance</M> — the last print is more than four days old. That covers a weekend
            plus a Monday holiday; past it the comparison stops meaning anything.</li>
          <li><M>pool below liquidity floor</M> — the pool prints a price nobody could actually trade at.</li>
          <li><M>pool read failed</M> — the RPC did not answer. We say so instead of reusing the last value.</li>
        </ul>
      </S>

      <S h="Sampling">
        Every five minutes, all listed tokens, continuously — including overnight and at weekends, which is the point.
        Aggregates use printed samples only. Session and closed-market samples are never pooled.
      </S>

      <S h="Market hours">
        NYSE regular session, 09:30–16:00 ET, Monday to Friday, with US market holidays and 13:00 early closes accounted
        for. That is <M>32.5</M> of <M>168</M> hours; the other <M>135.5</M> — <M>80.7%</M> of the week — is when these
        tokens trade without an open market behind them.
      </S>

      <S h="Known limits">
        The reference feed is a third party and can be delayed, rate-limited or wrong; when it fails we withhold rather
        than estimate. Pre-market and after-hours equity trading is not modelled — "last print" means the last regular
        session print. History only exists from the moment collection started, and a chart never extends past the data.
      </S>

      <S h="What this is not">
        Offbell is a measurement, not a recommendation. Nothing here is a signal, a forecast or a claim that a gap is
        tradeable. Stock tokens are on-chain tokens tracking an equity; holding one is not ownership of the underlying
        share.
      </S>
    </main>
  )
}
